import React, { useState, useEffect } from 'react';
import { myAxios } from '../../services/Helper';
import { isValidToken, getUserId } from '../../services/tokenService';
import './BookingHistoryStyles.css';
import './BookingInvoiceStyles.css';
import BookingInvoice from './BookingInvoice';

const BookingHistory = () => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [showInvoice, setShowInvoice] = useState(false);

    // Map backend status values to display-friendly values and colors
    const statusMapping = {
        'PENDING': { display: 'Pending', class: 'pending' },
        'CONFIRMED': { display: 'Confirmed', class: 'confirmed' },
        'CANCELLED': { display: 'Cancelled', class: 'cancelled' },
        'COMPLETED': { display: 'Completed', class: 'completed' },
        'pending': { display: 'Pending', class: 'pending' },
        'confirmed': { display: 'Confirmed', class: 'confirmed' },
        'cancelled': { display: 'Cancelled', class: 'cancelled' },
        'completed': { display: 'Completed', class: 'completed' }
    };

    // Normalize the status for display
    const getStatusInfo = (booking) => {
        const status = (booking.bookingStatus || booking.status || booking.booking_status || 'PENDING').toUpperCase();
        return statusMapping[status] || { display: status, class: 'pending' };
    };

    useEffect(() => {
        const fetchBookings = async () => {
            try {
                setLoading(true);

                // Check if user is authenticated
                const isAuthenticated = isValidToken();
                const userId = getUserId();

                if (isAuthenticated && userId) {
                    try {
                        // Try to fetch bookings from API first
                        const response = await myAxios.get('/api/booking/user-bookings');
                        console.log('Bookings API response:', response);

                        if (response.data && Array.isArray(response.data)) {
                            console.log('Bookings from API:', response.data);

                            // Check if we got any bookings
                            if (response.data.length > 0) {
                                setBookings(response.data);
                                // Also update sessionStorage to keep it in sync
                                sessionStorage.setItem('bookings', JSON.stringify(response.data));
                            } else {
                                console.log('No bookings found in API response');
                                // Try fetching from sessionStorage as fallback
                                const storedBookings = sessionStorage.getItem('bookings');
                                if (storedBookings) {
                                    const parsedBookings = JSON.parse(storedBookings);
                                    console.log('Using stored bookings:', parsedBookings);
                                    setBookings(parsedBookings);
                                } else {
                                    setBookings([]);
                                }
                            }
                        } else {
                            console.warn('API response is not an array:', response.data);
                            // Fallback to sessionStorage if API doesn't return expected format
                            const storedBookings = sessionStorage.getItem('bookings');
                            if (storedBookings) {
                                setBookings(JSON.parse(storedBookings));
                            }
                        }
                    } catch (apiError) {
                        console.error('Error fetching bookings from API:', apiError);
                        // Fallback to sessionStorage on API error
                        const storedBookings = sessionStorage.getItem('bookings');
                        if (storedBookings) {
                            setBookings(JSON.parse(storedBookings));
                        }
                    }
                } else {
                    // Guest user - check for guest bookings
                    const guestBookings = sessionStorage.getItem('guestBookings');
                    if (guestBookings) {
                        setBookings(JSON.parse(guestBookings));
                    } else {
                        // As a fallback, check 'bookings' as well
                        const storedBookings = sessionStorage.getItem('bookings');
                        if (storedBookings) {
                            setBookings(JSON.parse(storedBookings));
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching bookings:', error);
                setBookings([]);
            } finally {
                setLoading(false);
            }
        };

        fetchBookings();
    }, []);

    const handleViewInvoice = (booking) => {
        setSelectedBooking(booking);
        setShowInvoice(true);
    };

    const handleCloseInvoice = () => {
        setShowInvoice(false);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'No date provided';
        try {
            const date = new Date(dateString);

            // Check if the date is valid
            if (isNaN(date.getTime())) {
                console.error('Invalid date value:', dateString);
                return 'Date unavailable';
            }

            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            });
        } catch (error) {
            console.error('Error formatting date:', error, dateString);
            return 'Date unavailable';
        }
    };

    const formatTime = (timeString) => {
        if (!timeString) return '';

        // Handle HH:MM:SS format
        if (timeString.includes(':')) {
            // If it's already in HH:MM:SS format, convert to 12-hour format
            const timeParts = timeString.split(':');
            if (timeParts.length >= 2) {
                const hours = parseInt(timeParts[0]);
                const minutes = timeParts[1];
                const period = hours >= 12 ? 'PM' : 'AM';
                const hour12 = hours % 12 || 12;
                return `${hour12}:${minutes} ${period}`;
            }
        }

        return timeString;
    };

    return (
        <div className="bh-booking-history-container">
            <h1 className="bh-booking-history-title">My Bookings</h1>

            {loading ? (
                <div className="bh-loading-indicator">Loading your bookings...</div>
            ) : bookings.length === 0 ? (
                <div className="bh-no-bookings">
                    <p>You don't have any bookings yet.</p>
                    <p>Book a service to see your booking history here.</p>
                </div>
            ) : (
                <div className="bh-bookings-list">
                    {bookings.map((booking, index) => {
                        const statusInfo = getStatusInfo(booking);
                        return (
                            <div className="bh-booking-card" key={index}>
                                <div className="bh-booking-header">
                                    <h2>{booking.functionName ||
                                        (booking.function_id ? booking.function_id.functionName : null) ||
                                        booking.occasion ||
                                        booking.selectedOccasion ||
                                        'Lawn Service'}</h2>
                                    <span className={`bh-booking-status bh-${statusInfo.class}`}>
                                        {statusInfo.display}
                                    </span>
                                </div>

                                <div className="bh-booking-details">
                                    <div className="bh-booking-detail">
                                        <span className="bh-detail-label">Date:</span>
                                        <span className="bh-detail-value">{formatDate(booking.bookingForDate || booking.date || booking.booking_for_date)}</span>
                                    </div>

                                    <div className="bh-booking-detail">
                                        <span className="bh-detail-label">Time:</span>
                                        <span className="bh-detail-value">
                                            {booking.startTime && booking.endTime ?
                                                `${formatTime(booking.startTime)} - ${formatTime(booking.endTime)}` :
                                                booking.start_time && booking.end_time ?
                                                    `${formatTime(booking.start_time)} - ${formatTime(booking.end_time)}` :
                                                    booking.time || 'Not specified'}
                                        </span>
                                    </div>

                                    <div className="bh-booking-detail">
                                        <span className="bh-detail-label">Booking ID:</span>
                                        <span className="bh-detail-value">{booking.bookingId || booking.id || booking.bookingID || `BK-${index + 1000}`}</span>
                                    </div>

                                    <div className="bh-booking-detail">
                                        <span className="bh-detail-label">Total Amount:</span>
                                        <span className="bh-detail-value">₹{(booking.totalPaymentAmount || booking.totalAmount || booking.total || 0).toLocaleString()}</span>
                                    </div>

                                    {booking.paymentMode && (
                                        <div className="bh-booking-detail">
                                            <span className="bh-detail-label">Payment Mode:</span>
                                            <span className="bh-detail-value">{booking.paymentMode}</span>
                                        </div>
                                    )}
                                </div>

                                {(booking.subfunction && booking.subfunction.length > 0) || 
                                 (booking.decorations && booking.decorations.length > 0) ? (
                                    <div className="bh-booking-services">
                                        {booking.subfunction && booking.subfunction.length > 0 && (
                                            <div className="bh-booking-subfunctions">
                                                <h3>Services Included:</h3>
                                                <ul className="bh-subfunction-list">
                                                    {booking.subfunction.map((sub, idx) => (
                                                        <li key={idx} className="bh-subfunction-item">
                                                            <span className="bh-subfunction-name">{sub.subfunctionName}</span>
                                                            <span className="bh-subfunction-price">₹{sub.price.toLocaleString()}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        
                                        {booking.decorations && booking.decorations.length > 0 && (
                                            <div className="bh-booking-decorations">
                                                <h3>Decorations:</h3>
                                                <ul className="bh-decorations-list">
                                                    {booking.decorations.map((deco, idx) => (
                                                        <li key={idx} className="bh-decoration-item">
                                                            <span className="bh-decoration-name">{deco.name}</span>
                                                            <span className="bh-decoration-price">₹{Number(deco.price).toLocaleString()}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        
                                        {booking.items && booking.items.length > 0 && (
                                            <div className="bh-booking-items">
                                                <h3>Items Included:</h3>
                                                <ul className="bh-items-list">
                                                    {booking.items.map((item, idx) => (
                                                        <li key={idx} className="bh-item">
                                                            {item.name}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                ) : null}

                                <div className="bh-booking-actions">
                                    <button
                                        className="bh-view-invoice-btn"
                                        onClick={() => handleViewInvoice(booking)}
                                    >
                                        View Details
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {showInvoice && selectedBooking && (
                <BookingInvoice
                    booking={selectedBooking}
                    onClose={handleCloseInvoice}
                />
            )}
        </div>
    );
};

export default BookingHistory; 